import { whereEq } from 'ramda';
import type { Location } from 'slate';
import { Editor, Range, Text } from 'slate';

import type {
  AlineText,
  EditingMode,
  FontSizeFormat,
  ToggleableKeys,
  VerticalAlign,
} from '../internal';
import { AlineTransforms, defaultFontSize, findFont, Queries } from '../internal';

function normalizeFontSize(fontSizeValue: number): number {
  const roundedDown = Math.floor(fontSizeValue);

  const decimal = fontSizeValue - roundedDown;

  if (fontSizeValue < 1) return 1;
  if (fontSizeValue > 400) return 400;
  if ((fontSizeValue >= 1 || fontSizeValue <= 400) && decimal < 0.5) return roundedDown;
  return roundedDown + 0.5;
}

function getCurrentFontSize(editor: Editor): number {
  if (editor.selection?.listMarker)
    return Queries.activeListMarkerAtLocation(editor)?.fontSize.size ?? defaultFontSize;

  const [textEntry] = Editor.nodes<AlineText>(editor, { match: Text.isText, voids: true });
  return textEntry ? Queries.activeFontSizePt(textEntry[0]) : defaultFontSize;
}

function applyFontSizeChange(
  editor: Editor,
  options: {
    authorId: string;
    changeFontSize: (existingFontSize: number) => number;
    editingMode: EditingMode;
    voids?: boolean;
  }
): void {
  AlineTransforms.withoutNormalizing(editor, () => {
    const { authorId, changeFontSize, editingMode, voids = true } = options;
    if (!editor.selection) return;
    if (Range.isCollapsed(editor.selection)) {
      /**
       * If editor.marks is carrying cursor styles, we must use those to increment/decrement.  Otherwise use the relevant text node.
       */
      if (!editor.marks?.fontSize?.size) {
        const newFontSize: FontSizeFormat = {
          origin: 'USER_APPLIED',
          size: changeFontSize(getCurrentFontSize(editor)),
        };

        AlineTransforms.changeFontSize(editor, { authorId, editingMode, newFontSize, voids });
      } else {
        const changedMarkFontSize: FontSizeFormat = {
          origin: 'USER_APPLIED',
          size: changeFontSize(editor.marks.fontSize.size),
        };

        AlineTransforms.changeFontSize(editor, {
          authorId,
          editingMode,
          newFontSize: changedMarkFontSize,
          voids,
        });
      }

      return;
    }

    /**
     * With an active grid (table cell rectangle) selection, resize each selected cell's text nodes
     * individually (Queries.nodes iterates per cell). Whole cells are covered, so no edge splitting
     * is needed.
     */
    if (Queries.getIsGridSelection(editor)) {
      for (const [textNode, textPath] of Queries.nodes<AlineText>(editor, {
        match: Text.isText,
        voids,
      })) {
        const newFontSize: FontSizeFormat = {
          origin: 'USER_APPLIED',
          size: changeFontSize(Queries.activeFontSizePt(textNode)),
        };

        AlineTransforms.changeFontSize(editor, {
          at: textPath,
          authorId,
          editingMode,
          newFontSize,
          voids,
        });
      }

      return;
    }

    const rangeAfterSplit = AlineTransforms.splitTextAtEdges(editor, { editingMode });

    if (!rangeAfterSplit) return;

    const textNodeEntries = Editor.nodes<AlineText>(editor, {
      at: rangeAfterSplit,
      match: Text.isText,
      voids,
    });

    for (const textNodeEntry of textNodeEntries) {
      const [textNode, textPath] = textNodeEntry;

      const fontSizePt = Queries.activeFontSizePt(textNode);

      const newFontSize: FontSizeFormat = {
        origin: 'USER_APPLIED',
        size: changeFontSize(fontSizePt),
      };

      AlineTransforms.changeFontSize(editor, {
        at: textPath,
        authorId,
        editingMode,
        newFontSize,
        voids,
      });
    }

    // We set the selection to the rangeAfterSplit since, under the hood when doing multiple splits at different points, Slate doesn't set the selection to where we want.
    AlineTransforms.select(editor, rangeAfterSplit);
  });
}

const FormatTransforms = {
  changeFontSize: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      newFontSize: FontSizeFormat;
      at?: Location;
      voids?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, newFontSize, voids = true } = options;
      if (!at) return;

      newFontSize.size = normalizeFontSize(newFontSize.size);

      AlineTransforms.setTextProps(editor, {
        at,
        authorId,
        editingMode,
        textProps: { fontSize: newFontSize },
        voids,
      });
    });
  },
  /**
   * Decrement font size by a unit.
   */
  decrementFontSize: (
    editor: Editor,
    options: { authorId: string; editingMode: EditingMode; unit?: number; voids?: boolean }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, unit = 1, voids = true } = options;

      applyFontSizeChange(editor, {
        authorId,
        changeFontSize: (existingFontSize: number) => existingFontSize - unit,
        editingMode,
        voids,
      });
    });
  },
  /**
   * Increment font size by a unit.
   */
  incrementFontSize: (
    editor: Editor,
    options: { authorId: string; editingMode: EditingMode; unit?: number; voids?: boolean }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, unit = 1, voids = true } = options;

      applyFontSizeChange(editor, {
        authorId,
        changeFontSize: (existingFontSize: number) => existingFontSize + unit,
        editingMode,
        voids,
      });
    });
  },
  setFontType: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      type: AlineText['fontType']['type'];
      at?: Location;
      voids?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, type, voids = true } = options;
      if (!at) return;

      const {
        fontDetails: { fontFamily },
      } = findFont(type);
      const newFontType: Pick<AlineText, 'fontType'> = {
        fontType: { fontFamily, origin: 'USER_APPLIED', type },
      };

      AlineTransforms.setTextProps(editor, {
        at,
        authorId,
        editingMode,
        textProps: newFontType,
        voids,
      });
    });
  },

  /**
   * TODO: If changing this method, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-396.
   */
  toggleTextFormatting: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      formatKey: ToggleableKeys;
      at?: Location;
      voids?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      let { at = editor.selection } = options;
      const { authorId, editingMode, formatKey, voids = true } = options;
      if (!at) return;

      /**
       * A grid (table cell rectangle) selection is not unhung: `at` keeps referring to the live
       * selection, so isTextFormatActive samples and setTextProps applies per selected cell rather
       * than over the linear range (which would also cover cells outside the rectangle).
       */
      const isGridSelection = !!Queries.getGridSelectionInfoForLocation(editor, { at });

      if (!isGridSelection && Range.isRange(at)) {
        const unhungRange = AlineTransforms.unhangRange(editor, { at });
        if (!unhungRange) return;

        at = unhungRange;
      }

      const formatActiveOnAllTextNodes = Queries.isTextFormatActive(editor, formatKey, {
        at,
        listMarker: true,
        voids,
      });
      const newFormatValue: AlineText[ToggleableKeys] = formatActiveOnAllTextNodes
        ? { active: false, origin: 'USER_APPLIED' }
        : { active: true, origin: 'USER_APPLIED' };

      AlineTransforms.setTextProps(editor, {
        at,
        authorId,
        editingMode,
        textProps: { [formatKey]: newFormatValue },
        voids,
      });
    });
  },
  toggleVerticalAlign: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      verticalAlign: VerticalAlign;
      at?: Location;
      voids?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      let { at = editor.selection } = options;
      const { authorId, editingMode, verticalAlign, voids = true } = options;
      if (!at) return;

      // A grid selection is not unhung so sampling and applying stay per selected cell (see
      // toggleTextFormatting for details).
      const isGridSelection = !!Queries.getGridSelectionInfoForLocation(editor, { at });

      if (!isGridSelection && Range.isRange(at)) {
        const unhungRange = AlineTransforms.unhangRange(editor, { at });
        if (!unhungRange) return;

        at = unhungRange;
      }

      const formatActiveOnAllTextNodes = Queries.isTextFormatActive(editor, 'verticalAlign', {
        at,
        voids,
      });

      const formats = Queries.formats(editor, { at, voids });
      const isSameFormat = (Array.isArray(formats) ? formats : [formats]).some(format => {
        const renderedFormat = format && Queries.renderedFormat('verticalAlign', format);
        return Boolean(renderedFormat) && whereEq({ align: verticalAlign }, renderedFormat);
      });

      const newFormatValue: AlineText['verticalAlign'] =
        formatActiveOnAllTextNodes && isSameFormat
          ? {
              align: 'baseline',
              origin: 'USER_APPLIED',
            }
          : {
              align: verticalAlign,
              origin: 'USER_APPLIED',
            };

      AlineTransforms.setTextProps(editor, {
        at,
        authorId,
        editingMode,
        textProps: { verticalAlign: newFormatValue },
        voids,
      });
    });
  },
};

export default FormatTransforms;
